import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import {
  Users,
  IdentificationBadge,
  Clock,
  Calendar,
  Image,
  CheckCircle,
  Timer,
  TrendUp,
  TrendDown,
  Scales,
} from "@phosphor-icons/react";

function FinanceCard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/finanzas/resumen").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  const maxVal = Math.max(1, ...data.por_mes.map((m) => Math.max(m.ingresos, m.gastos)));
  const MESES = ["E", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];
  const benColor = data.mes_beneficio >= 0 ? "text-green-700" : "text-red-700";
  const ytdColor = data.ytd_beneficio >= 0 ? "text-green-700" : "text-red-700";
  return (
    <Card className="p-6 mt-6 border-slate-200 col-span-full" data-testid="finance-card">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("finance.title")}</div>
          <div className="text-lg heading-font font-bold text-slate-900">{t("finance.subtitle", { anio: data.anio })}</div>
        </div>
      </div>

      {/* KPIs current month + YTD */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-3" data-testid="kpi-ingresos">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-green-700">
            <TrendUp size={14} weight="bold" /> {t("finance.mesIngresos")}
          </div>
          <div className="text-2xl heading-font font-bold text-green-700 mt-1">€ {data.mes_ingresos.toFixed(2)}</div>
          <div className="text-[10px] text-slate-500 mt-1">YTD € {data.ytd_ingresos.toFixed(2)}</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="kpi-gastos">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-red-700">
            <TrendDown size={14} weight="bold" /> {t("finance.mesGastos")}
          </div>
          <div className="text-2xl heading-font font-bold text-red-700 mt-1">€ {data.mes_gastos.toFixed(2)}</div>
          <div className="text-[10px] text-slate-500 mt-1">YTD € {data.ytd_gastos.toFixed(2)}</div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 col-span-2 md:col-span-1" data-testid="kpi-beneficio">
          <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${benColor}`}>
            <Scales size={14} weight="bold" /> {t("finance.mesBeneficio")}
          </div>
          <div className={`text-2xl heading-font font-bold mt-1 ${benColor}`}>€ {data.mes_beneficio.toFixed(2)}</div>
          <div className={`text-[10px] mt-1 ${ytdColor}`}>YTD € {data.ytd_beneficio.toFixed(2)}</div>
        </div>
      </div>

      {/* Mini bar chart per month */}
      <div className="mt-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">{t("finance.monthly")}</div>
        <div className="flex items-end gap-1 h-28">
          {data.por_mes.map((m) => {
            const hIn = (m.ingresos / maxVal) * 100;
            const hGa = (m.gastos / maxVal) * 100;
            const isCurrent = m.mes === data.current_month;
            return (
              <div key={m.mes} className="flex-1 flex flex-col items-center gap-0.5">
                <div className="w-full flex items-end justify-center gap-0.5 h-24">
                  <div
                    className="w-1/2 bg-green-500 rounded-t transition-all"
                    style={{ height: `${hIn}%` }}
                    title={`${t("finance.ingresos")}: € ${m.ingresos.toFixed(2)}`}
                  />
                  <div
                    className="w-1/2 bg-red-500 rounded-t transition-all"
                    style={{ height: `${hGa}%` }}
                    title={`${t("finance.gastos")}: € ${m.gastos.toFixed(2)}`}
                  />
                </div>
                <div className={`text-[9px] font-semibold ${isCurrent ? "text-slate-900" : "text-slate-400"}`}>{MESES[m.mes - 1]}</div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-center gap-4 mt-2 text-[10px] text-slate-500">
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-green-500" /> {t("finance.ingresos")}</div>
          <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-red-500" /> {t("finance.gastos")}</div>
        </div>
      </div>
    </Card>
  );
}

function StatCard({ icon: Icon, label, value, color, testid }) {
  const bgColor = {
    green: "bg-green-50 text-green-700",
    red: "bg-red-50 text-red-700",
    blue: "bg-blue-50 text-blue-700",
    amber: "bg-amber-50 text-amber-700",
    slate: "bg-slate-100 text-slate-700",
  }[color];

  return (
    <Card className="p-6 card-hover border-slate-200" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">{label}</div>
          <div className="text-3xl heading-font font-bold text-slate-900 mt-2">{value}</div>
        </div>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${bgColor}`}>
          <Icon size={22} weight="duotone" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user, isAdmin } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/stats")
      .then((res) => setStats(res.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="text-3xl heading-font font-bold text-slate-900">
          {t("dashboard.welcome")}, {user?.name}
        </h1>
        <p className="text-slate-500 mt-1 text-sm">{new Date().toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="p-6 h-32 animate-pulse bg-slate-100 border-slate-200" />
          ))}
        </div>
      ) : isAdmin ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard icon={IdentificationBadge} label={t("dashboard.employees")} value={stats.total_funcionarios} color="green" testid="stat-employees" />
            <StatCard icon={Users} label={t("dashboard.clients")} value={stats.total_clientes} color="blue" testid="stat-clients" />
            <StatCard icon={Clock} label={t("dashboard.pendingHours")} value={stats.horas_pendentes} color="amber" testid="stat-pending-hours" />
            <StatCard icon={Calendar} label={t("dashboard.todayAgenda")} value={stats.agenda_hoje} color="red" testid="stat-agenda-today" />
            <StatCard icon={Image} label={t("dashboard.photos")} value={stats.total_fotos} color="slate" testid="stat-photos" />
          </div>
          <FinanceCard />
        </>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard icon={Timer} label={t("dashboard.myPending")} value={stats.horas_pendentes} color="amber" testid="stat-my-pending" />
          <StatCard icon={CheckCircle} label={t("dashboard.myApproved")} value={stats.horas_aprovadas} color="green" testid="stat-my-approved" />
          <StatCard icon={Calendar} label={t("dashboard.myAgenda")} value={stats.agenda_hoje} color="red" testid="stat-my-agenda" />
          <StatCard icon={Image} label={t("dashboard.myPhotos")} value={stats.total_fotos} color="blue" testid="stat-my-photos" />
          <StatCard icon={Clock} label={t("dashboard.totalHours")} value={`${stats.total_horas_trabajadas} h`} color="slate" testid="stat-total-hours" />
        </div>
      )}
    </div>
  );
}
